import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getComments, postComment } from '../../services/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MessageSquare, User, Send } from 'lucide-react';

export default function CommentSection({ articleId }) {
  const { t, isKannada } = useLanguage();
  const { user, login } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (articleId) {
      getComments(articleId).then(res => setComments(res.data)).catch(() => {});
    }
  }, [articleId]);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setLoading(true);
    try {
      const res = await postComment(articleId, { content: newComment });
      setComments(prev => [res.data, ...prev]);
      setNewComment('');
    } catch (err) { console.error('Comment error:', err); }
    finally { setLoading(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="mt-10 pt-8 border-t border-border/50" data-testid="comment-section">
      <h3 className={`flex items-center gap-2.5 text-lg font-bold text-foreground mb-7 ${isKannada ? 'font-kannada' : 'font-heading'}`}>
        <div className="w-9 h-9 rounded-lg bg-palace-100 dark:bg-palace-900/30 flex items-center justify-center">
          <MessageSquare className="w-4 h-4 text-palace-500" />
        </div>
        {t('comments')} ({comments.length})
      </h3>

      {user ? (
        <div className="mb-7 flex gap-3" data-testid="comment-form">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-palace-400/20">
            {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> :
              <div className="w-full h-full bg-palace-100 dark:bg-palace-900 flex items-center justify-center"><User className="w-4 h-4" /></div>}
          </div>
          <div className="flex-1">
            <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
              placeholder={t('writeComment')}
              className="mb-2 bg-card border-border/50 rounded-xl min-h-[80px] focus:ring-palace-400"
              data-testid="comment-textarea" />
            <Button onClick={handleSubmit} disabled={loading || !newComment.trim()}
              className="metallic-gold text-palace-900 font-bold rounded-lg magnetic-btn" data-testid="post-comment-btn">
              <Send className="w-4 h-4 mr-2" />{t('postComment')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mb-7 p-5 rounded-xl glass-card text-center">
          <button onClick={login} className="text-palace-500 font-bold hover:underline" data-testid="login-to-comment-btn">
            {t('login')} to comment
          </button>
        </div>
      )}

      <div className="space-y-4">
        {comments.map(comment => (
          <div key={comment.comment_id} className="flex gap-3 p-4 rounded-xl neu-card" data-testid={`comment-${comment.comment_id}`}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ring-palace-300/20">
              {comment.user?.picture ? <img src={comment.user.picture} alt="" className="w-full h-full object-cover" /> :
                <div className="w-full h-full bg-palace-100 dark:bg-palace-900 flex items-center justify-center"><User className="w-4 h-4 text-muted-foreground" /></div>}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-foreground">{comment.user?.name || 'User'}</span>
                <span className="text-[10px] text-muted-foreground">{formatDate(comment.created_at)}</span>
              </div>
              <p className="text-sm text-foreground/70 leading-relaxed">{comment.content}</p>
            </div>
          </div>
        ))}
        {comments.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-8">No comments yet. Be the first to share your thoughts!</p>
        )}
      </div>
    </div>
  );
}
